import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Helmet } from "react-helmet-async";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  EmailOutlined,
  NotificationsActiveOutlined,
  Replay,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  endOfDay,
  endOfMonth,
  startOfDay,
  startOfMonth,
  subDays,
  subMonths,
} from "date-fns";
import {
  getNotificationRecord,
  listNotificationRecords,
  resendNotificationRecord,
  resendNotificationRecords,
} from "../utils/deliveryRecordsApi";
import { getCurrentUser, getUserPermissions } from "../utils/permissionsUtils";

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const STATUSES = ["pending", "sending", "sent", "uncertain", "failed"];

const statusColors = {
  pending: "default",
  sending: "info",
  sent: "success",
  uncertain: "warning",
  failed: "error",
};

const CHANNELS = {
  email: { Icon: EmailOutlined, label: "Email" },
  push: { Icon: NotificationsActiveOutlined, label: "Push" },
};

const ChannelIcon = ({ channel }) => {
  const config = CHANNELS[channel];
  if (!config) return channel;
  const { Icon, label } = config;
  return (
    <Tooltip title={label}>
      <Icon fontSize="small" color="action" aria-label={label} />
    </Tooltip>
  );
};

/** Only these can be resent; pending and sending rows are already on their way. */
const RESENDABLE_STATUSES = ["sent", "failed", "uncertain"];

const isResendable = (record) =>
  RESENDABLE_STATUSES.includes(record.status) &&
  new Date(record.expired_at) > new Date();

const PAGE_SIZES = [10, 20, 50, 100];

/** Compact sizing so the table, with its selection column, fits on one screen. */
const compactField = {
  "& .MuiInputBase-root": { fontSize: 13 },
  "& .MuiInputLabel-root": { fontSize: 13 },
  "& .MuiFormHelperText-root": { fontSize: 11 },
};
const compactTable = {
  "& .MuiTableCell-root": { fontSize: 12, py: 0.5, px: 1 },
  "& .MuiTableCell-head": { fontWeight: 600 },
  "& .MuiChip-root": { height: 20, fontSize: 11 },
  "& .MuiButton-root": { fontSize: 12, minWidth: 0 },
};

// The backend allows resends for these admin roles only.
const canResendRole = () =>
  getUserPermissions().isSuperAdmin || getCurrentUser()?.role === "operator";

/** Date range presets; a preset's `range()` is computed when it is selected. */
const DATE_PRESETS = [
  {
    value: "today",
    label: "Today",
    range: () => [startOfDay(new Date()), endOfDay(new Date())],
  },
  {
    value: "yesterday",
    label: "Yesterday",
    range: () => {
      const yesterday = subDays(new Date(), 1);
      return [startOfDay(yesterday), endOfDay(yesterday)];
    },
  },
  {
    value: "last7",
    label: "Last 7 days",
    range: () => [startOfDay(subDays(new Date(), 6)), endOfDay(new Date())],
  },
  {
    value: "last30",
    label: "Last 30 days",
    range: () => [startOfDay(subDays(new Date(), 29)), endOfDay(new Date())],
  },
  {
    value: "thisMonth",
    label: "This month",
    range: () => [startOfMonth(new Date()), endOfDay(new Date())],
  },
  {
    value: "lastMonth",
    label: "Last month",
    range: () => {
      const lastMonth = subMonths(new Date(), 1);
      return [startOfMonth(lastMonth), endOfMonth(lastMonth)];
    },
  },
  { value: "custom", label: "Custom range" },
];

const COLUMNS = [
  { id: "created_at", label: "Created", sortable: true },
  { id: "email_type", label: "Type", sortable: true },
  { id: "gh_channel_id", label: "Channel", sortable: true },
  { id: "user", label: "User" },
  { id: "sqs_message_id", label: "SQS message ID" },
  { id: "status", label: "Status", sortable: true },
  { id: "attempt_count", label: "Attempts", sortable: true },
  { id: "sent_at", label: "Sent", sortable: true },
  { id: "actions", label: "" },
];

const formatDate = (value) => {
  if (!value) return "—";
  return new Date(value).toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

const DetailField = ({ label, children }) => (
  <Grid item xs={12} sm={6}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
      {children ?? "—"}
    </Typography>
  </Grid>
);

const JsonBlock = ({ label, value }) => (
  <Box>
    <Typography variant="subtitle2" gutterBottom>
      {label}
    </Typography>
    <Box
      component="pre"
      sx={{
        m: 0,
        p: 1.5,
        bgcolor: "grey.100",
        borderRadius: 1,
        fontSize: 12,
        overflowX: "auto",
      }}
    >
      {value ? JSON.stringify(value, null, 2) : "—"}
    </Box>
  </Box>
);

const NotificationRecordsPage = () => {
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalIsCapped, setTotalIsCapped] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [resendingId, setResendingId] = useState(null);
  // Ticked rows on the current page; cleared whenever the page's rows change.
  const [checkedIds, setCheckedIds] = useState(() => new Set());
  const canResend = useMemo(canResendRole, []);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [sort, setSort] = useState({ column: "created_at", order: "desc" });
  const [selected, setSelected] = useState(null);
  // Payload and context load only when a record is opened.
  const [detail, setDetail] = useState({
    loading: false,
    error: null,
    record: null,
  });
  const openedRecordId = useRef(null);

  const [filters, setFilters] = useState({
    channel: "",
    status: "",
    userOrTeamId: "",
    datePreset: "last7",
    customFrom: startOfDay(subDays(new Date(), 6)),
    customTo: endOfDay(new Date()),
  });
  const [debouncedId, setDebouncedId] = useState("");

  const idInput = filters.userOrTeamId.trim();
  const idIsInvalid = idInput !== "" && !UUID_PATTERN.test(idInput);

  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedId(UUID_PATTERN.test(idInput) ? idInput : ""),
      500
    );
    return () => clearTimeout(timer);
  }, [idInput]);

  const dateRange = useMemo(() => {
    if (filters.datePreset === "custom") {
      return [
        filters.customFrom ? startOfDay(filters.customFrom) : null,
        filters.customTo ? endOfDay(filters.customTo) : null,
      ];
    }
    const preset = DATE_PRESETS.find(
      (item) => item.value === filters.datePreset
    );
    return preset.range();
  }, [filters.datePreset, filters.customFrom, filters.customTo]);

  const fetchRecords = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [from, to] = dateRange;
      const result = await listNotificationRecords({
        channel: filters.channel,
        status: filters.status,
        userOrTeamId: debouncedId,
        from: from && !Number.isNaN(from.getTime()) ? from.toISOString() : "",
        to: to && !Number.isNaN(to.getTime()) ? to.toISOString() : "",
        sort: sort.column,
        order: sort.order,
        page: page + 1,
        limit: rowsPerPage,
      });
      setRecords(result.notifications);
      setCheckedIds(new Set());
      setTotal(result.total);
      setTotalIsCapped(result.totalIsCapped);
    } catch (err) {
      console.error("Failed to fetch notification records:", err);
      setError("Failed to load notification records. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [
    dateRange,
    filters.channel,
    filters.status,
    debouncedId,
    sort,
    page,
    rowsPerPage,
  ]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPage(0);
  };

  const handleSort = (column) => {
    setSort((prev) => ({
      column,
      order: prev.column === column && prev.order === "desc" ? "asc" : "desc",
    }));
    setPage(0);
  };

  const resendableOnPage = records.filter(isResendable);
  const allChecked =
    resendableOnPage.length > 0 &&
    resendableOnPage.every((record) => checkedIds.has(record.id));
  const someChecked = checkedIds.size > 0 && !allChecked;

  const toggleAll = () =>
    setCheckedIds(
      allChecked
        ? new Set()
        : new Set(resendableOnPage.map((record) => record.id))
    );

  const toggleOne = (event, id) => {
    event.stopPropagation();
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleResendChecked = async () => {
    const ids = [...checkedIds];
    if (
      !window.confirm(
        `Resend ${ids.length} notification${
          ids.length === 1 ? "" : "s"
        }? Each user will receive it again.`
      )
    ) {
      return;
    }

    setResendingId("bulk");
    setError(null);
    setNotice(null);
    try {
      const { resent, skipped } = await resendNotificationRecords(ids);
      setNotice(
        `Resend queued for ${resent.length} notification${
          resent.length === 1 ? "" : "s"
        }.` +
          (skipped.length
            ? ` Skipped ${skipped.length}: ${[
                ...new Set(skipped.map((item) => item.reason)),
              ].join("; ")}`
            : "")
      );
      await fetchRecords();
    } catch (err) {
      setError(
        err?.response?.data?.message || "Failed to resend the notifications."
      );
    } finally {
      setResendingId(null);
    }
  };

  const handleResend = async (event, record) => {
    // The row itself opens the detail dialog.
    event.stopPropagation();
    const channel =
      CHANNELS[record.gh_channel_id]?.label ?? record.gh_channel_id;
    if (
      !window.confirm(
        `Resend this ${record.email_type} ${channel.toLowerCase()} to ${
          record.username || record.user_id
        }? The user will receive it again.`
      )
    ) {
      return;
    }

    setResendingId(record.id);
    setError(null);
    setNotice(null);
    try {
      await resendNotificationRecord(record.id);
      setNotice(`Resend queued for ${record.email_type} (${channel}).`);
      await fetchRecords();
    } catch (err) {
      setError(
        err?.response?.data?.message || "Failed to resend the notification."
      );
    } finally {
      setResendingId(null);
    }
  };

  const openRecord = async (record) => {
    openedRecordId.current = record.id;
    setSelected(record);
    setDetail({ loading: true, error: null, record: null });
    try {
      const full = await getNotificationRecord(record.id);
      // A slower response for a previously opened row must not overwrite this one.
      if (openedRecordId.current !== record.id) return;
      setDetail({ loading: false, error: null, record: full });
    } catch (err) {
      if (openedRecordId.current !== record.id) return;
      console.error("Failed to fetch notification record:", err);
      setDetail({
        loading: false,
        error: "Failed to load the payload. Please try again.",
        record: null,
      });
    }
  };

  const renderDetailDialog = () => {
    if (!selected) return null;

    return (
      <Dialog open onClose={() => setSelected(null)} maxWidth="md" fullWidth>
        <DialogTitle>Notification record</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <Chip label={selected.email_type} size="small" />
            <ChannelIcon channel={selected.gh_channel_id} />
            <Chip
              label={selected.status}
              color={statusColors[selected.status] || "default"}
              size="small"
            />
          </Box>

          <Grid container spacing={2}>
            <DetailField label="Record ID">{selected.id}</DetailField>
            <DetailField label="User ID">{selected.user_id}</DetailField>
            <DetailField label="Username">{selected.username}</DetailField>
            <DetailField label="Gaming Hub user ID">
              {selected.engagecraft_id}
            </DetailField>
            <DetailField label="Occurrence">
              {selected.occurrence_key}
            </DetailField>
            <DetailField label="Attempts">{selected.attempt_count}</DetailField>
            <DetailField label="Created">
              {formatDate(selected.created_at)}
            </DetailField>
            <DetailField label="Send attempted">
              {formatDate(selected.send_attempted_at)}
            </DetailField>
            <DetailField label="Sent">
              {formatDate(selected.sent_at)}
            </DetailField>
            <DetailField label="Expires">
              {formatDate(selected.expired_at)}
            </DetailField>
            <DetailField label="SQS message ID">
              {selected.sqs_message_id}
            </DetailField>
            <DetailField label="Last error">{selected.last_error}</DetailField>
          </Grid>

          <Divider sx={{ my: 2 }} />

          {detail.loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : detail.error ? (
            <Alert severity="error">{detail.error}</Alert>
          ) : (
            <Box sx={{ display: "grid", gap: 2 }}>
              <JsonBlock
                label="Payload sent to Gaming Hub"
                value={detail.record?.payload}
              />
              <JsonBlock label="Context" value={detail.record?.context} />
            </Box>
          )}
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <>
      <Helmet>
        <title>Notification Records | DreamTeam Admin</title>
      </Helmet>

      <Container maxWidth="xl">
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" gutterBottom>
            Notification Records
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Every email and push queued for Gaming Hub, with its delivery
            status. Click any row to see the exact payload that was sent.
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {notice && (
          <Alert
            severity="success"
            sx={{ mb: 3 }}
            onClose={() => setNotice(null)}
          >
            {notice}
          </Alert>
        )}

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1.5,
                  "& .MuiTextField-root": compactField,
                }}
              >
                <TextField
                  select
                  label="Channel"
                  value={filters.channel}
                  onChange={(e) =>
                    handleFilterChange("channel", e.target.value)
                  }
                  sx={{ minWidth: 130 }}
                  size="small"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="email">Email</MenuItem>
                  <MenuItem value="push">Push</MenuItem>
                </TextField>

                <TextField
                  select
                  label="Status"
                  value={filters.status}
                  onChange={(e) => handleFilterChange("status", e.target.value)}
                  sx={{ minWidth: 130 }}
                  size="small"
                >
                  <MenuItem value="">All</MenuItem>
                  {STATUSES.map((status) => (
                    <MenuItem key={status} value={status}>
                      {status}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  label="User or team ID"
                  value={filters.userOrTeamId}
                  onChange={(e) =>
                    handleFilterChange("userOrTeamId", e.target.value)
                  }
                  error={idIsInvalid}
                  helperText={
                    idIsInvalid ? "Enter a full user or team UUID" : " "
                  }
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  sx={{ minWidth: 340 }}
                  size="small"
                />

                <TextField
                  select
                  label="Date range"
                  value={filters.datePreset}
                  onChange={(e) =>
                    handleFilterChange("datePreset", e.target.value)
                  }
                  sx={{ minWidth: 160 }}
                  size="small"
                >
                  {DATE_PRESETS.map((preset) => (
                    <MenuItem key={preset.value} value={preset.value}>
                      {preset.label}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  label="Per page"
                  value={rowsPerPage}
                  onChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  sx={{ minWidth: 100 }}
                  size="small"
                >
                  {PAGE_SIZES.map((size) => (
                    <MenuItem key={size} value={size}>
                      {size}
                    </MenuItem>
                  ))}
                </TextField>

                {filters.datePreset === "custom" && (
                  <>
                    <DatePicker
                      label="From"
                      value={filters.customFrom}
                      maxDate={filters.customTo || undefined}
                      onChange={(value) =>
                        handleFilterChange("customFrom", value)
                      }
                      inputFormat="dd/MM/yyyy"
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          sx={{ width: 170 }}
                        />
                      )}
                    />
                    <DatePicker
                      label="To"
                      value={filters.customTo}
                      minDate={filters.customFrom || undefined}
                      onChange={(value) =>
                        handleFilterChange("customTo", value)
                      }
                      inputFormat="dd/MM/yyyy"
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          sx={{ width: 170 }}
                        />
                      )}
                    />
                  </>
                )}
              </Box>
            </LocalizationProvider>
          </CardContent>
        </Card>

        <Card>
          {checkedIds.size > 0 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                px: 2,
                py: 1,
                bgcolor: "action.selected",
              }}
            >
              <Typography variant="body2">
                {checkedIds.size} selected
              </Typography>
              <Button
                variant="text"
                size="small"
                startIcon={<Replay fontSize="small" />}
                disabled={!canResend || resendingId !== null}
                onClick={handleResendChecked}
              >
                {resendingId === "bulk" ? "Resending…" : "Resend selected"}
              </Button>
            </Box>
          )}
          <TableContainer component={Paper} variant="outlined">
            <Table size="small" sx={compactTable}>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={allChecked}
                      indeterminate={someChecked}
                      disabled={!canResend || resendableOnPage.length === 0}
                      onChange={toggleAll}
                      inputProps={{
                        "aria-label": "Select all resendable rows",
                      }}
                    />
                  </TableCell>
                  {COLUMNS.map((column) => (
                    <TableCell
                      key={column.id}
                      sortDirection={
                        sort.column === column.id ? sort.order : false
                      }
                    >
                      {column.sortable ? (
                        <TableSortLabel
                          active={sort.column === column.id}
                          direction={
                            sort.column === column.id ? sort.order : "desc"
                          }
                          onClick={() => handleSort(column.id)}
                        >
                          {column.label}
                        </TableSortLabel>
                      ) : (
                        column.label
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell
                      colSpan={COLUMNS.length + 1}
                      align="center"
                      sx={{ py: 4 }}
                    >
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : records.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={COLUMNS.length + 1}
                      align="center"
                      sx={{ py: 4 }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        No notification records found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  records.map((record) => (
                    <TableRow
                      key={record.id}
                      hover
                      onClick={() => openRecord(record)}
                      selected={checkedIds.has(record.id)}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={checkedIds.has(record.id)}
                          disabled={!canResend || !isResendable(record)}
                          onClick={(event) => toggleOne(event, record.id)}
                          inputProps={{ "aria-label": "Select row" }}
                        />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {formatDate(record.created_at)}
                      </TableCell>
                      <TableCell>{record.email_type}</TableCell>
                      <TableCell>
                        <ChannelIcon channel={record.gh_channel_id} />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ fontSize: 12 }}>
                          {record.username || "—"}
                        </Box>
                        <Box sx={{ fontSize: 11, color: "text.secondary" }}>
                          {record.user_id}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ wordBreak: "break-all" }}>
                        {record.sqs_message_id || "—"}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={record.status}
                          color={statusColors[record.status] || "default"}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{record.attempt_count}</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {formatDate(record.sent_at)}
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          variant="text"
                          size="small"
                          startIcon={<Replay fontSize="small" />}
                          disabled={
                            !canResend ||
                            resendingId !== null ||
                            !isResendable(record)
                          }
                          onClick={(event) => handleResend(event, record)}
                        >
                          {resendingId === record.id ? "Resending…" : "Resend"}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={totalIsCapped ? -1 : total}
            labelDisplayedRows={({ from, to }) =>
              `${from}–${to} of ${
                totalIsCapped
                  ? `more than ${total.toLocaleString("en-GB")}`
                  : total.toLocaleString("en-GB")
              }`
            }
            page={page}
            onPageChange={(event, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            // Page size is chosen with the "Per page" filter above the table.
            rowsPerPageOptions={[]}
            sx={{
              "& .MuiTablePagination-displayedRows": { fontSize: 12 },
            }}
          />
        </Card>
      </Container>

      {renderDetailDialog()}
    </>
  );
};

export default NotificationRecordsPage;
